import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";

@Injectable({
  providedIn: "root",
})
export class IeService {
  constructor(public toastService: ToastrService) { }

  private calculaDVPA(valor: string): boolean {
    const base = valor.substring(0, 8);
    const dvInformado = parseInt(valor.substring(8, 9), 10);

    const pesos = [9, 8, 7, 6, 5, 4, 3, 2];

    let soma = 0;

    for (let i = 0; i < 8; i++) {
      soma += parseInt(base[i], 10) * pesos[i];
    }

    const resto = soma % 11;
    const dvCalculado = resto < 2 ? 0 : 11 - resto;

    return dvCalculado === dvInformado;
  }

  private eIndefinido(objeto: any): boolean {
    return typeof objeto === "undefined";
  }

  private tamanhoNaoE(string, tamanho?): boolean {
    if (this.eIndefinido(tamanho)) {
      tamanho = 9;
    }
    return string.length !== tamanho;
  }

  private tamanhoE(string, tamanho): boolean {
    return !this.tamanhoNaoE(string, tamanho);
  }

  private serie(de: number, ate: number): number[] {
    const resultado = [];
    while (de <= ate) {
      resultado.push(de++);
    }
    return resultado;
  }

  private primeiros(string: string, quantidade?: number): string {
    if (this.eIndefinido(quantidade)) {
      quantidade = 8;
    }
    return string.substring(0, quantidade);
  }

  private substracaoPor11SeMaiorQue2CasoContrario0(valor: number): number {
    return valor < 2 ? 0 : 11 - valor;
  }

  private mod(
    valor: string,
    multiplicadores?: number[],
    divisor?: number
  ): number {
    if (this.eIndefinido(divisor)) {
      divisor = 11;
    }
    if (this.eIndefinido(multiplicadores)) {
      multiplicadores = this.serie(2, 9);
    }
    let i = 0;
    return (
      valor.split("").reduceRight((anterior, atual) => {
        if (i > multiplicadores.length - 1) {
          i = 0;
        }
        return multiplicadores[i++] * parseInt(atual, 10) + anterior;
      }, 0) % divisor
    );
  }

  private calculoTrivial(
    valor: string,
    base?: string,
    validarTamanho?: boolean
  ): boolean {
    if (!validarTamanho && this.tamanhoNaoE(valor)) {
      return false;
    }
    if (this.eIndefinido(base)) {
      base = this.primeiros(valor);
    }
    const digito = this.substracaoPor11SeMaiorQue2CasoContrario0(
      this.mod(base)
    );
    return valor === base + digito;
  }

  private naoComecaCom(string: string, valor: string): boolean {
    return string.substring(0, valor.length) !== valor;
  }

  private entre(
    valor: string | number,
    limiteInferior: number,
    limiteSuperior: number
  ): boolean {
    if (typeof valor === "string") {
      valor = parseInt(valor, 10);
    }
    return valor >= limiteInferior && valor <= limiteSuperior;
  }

  private funcoes: { [key: string]: (valor: string) => boolean } = {
    ba: (valor: string): boolean => {
      if (this.tamanhoNaoE(valor, 8) && this.tamanhoNaoE(valor)) {
        return false;
      }
      const base = this.primeiros(valor, valor.length - 2);
      let primeiroDigito, segundoDigito;

      const segundoMultiplicador = this.serie(2, 7);
      const primeiroMultiplicador = this.serie(2, 8);

      let primeiroResto, segundoResto;
      let digitoComparacao = valor.substring(0, 1);

      if (this.tamanhoE(valor, 9)) {
        segundoMultiplicador.push(8);
        primeiroMultiplicador.push(9);
        digitoComparacao = valor.substring(1, 2);
      }

      if ("0123458".split("").indexOf(digitoComparacao) !== -1) {
        segundoResto = this.mod(base, segundoMultiplicador, 10);
        segundoDigito = segundoResto === 0 ? 0 : 10 - segundoResto;

        primeiroResto = this.mod(
          base + segundoDigito,
          primeiroMultiplicador,
          10
        );
        primeiroDigito = primeiroResto === 0 ? 0 : 10 - primeiroResto;
      } else {
        segundoResto = this.mod(base, segundoMultiplicador);
        segundoDigito =
          this.substracaoPor11SeMaiorQue2CasoContrario0(segundoResto);

        primeiroResto = this.mod(base + segundoDigito, primeiroMultiplicador);
        primeiroDigito =
          this.substracaoPor11SeMaiorQue2CasoContrario0(primeiroResto);
      }

      return valor === base + primeiroDigito + segundoDigito;
    },

    se: (valor: string): boolean => {
      if (this.tamanhoNaoE(valor)) {
        return false;
      }
      return this.calculoTrivial(valor);
    },

    al: (valor: string): boolean => {
      if (this.tamanhoNaoE(valor)) {
        return false;
      }

      if (this.naoComecaCom(valor, "24")) {
        return false;
      }

      const base = this.primeiros(valor);

      const resto = this.mod(base) * 10;
      const modValue = resto - Math.floor(resto / 11) * 11;
      const digito = modValue === 10 ? 0 : modValue;

      return valor === base + digito;
    },

    pb: (valor): boolean => {
      if (this.tamanhoNaoE(valor)) {
        return false;
      }

      return this.calculoTrivial(valor);
    },

    rn: (valor): boolean => {
      if (this.tamanhoNaoE(valor) && this.tamanhoNaoE(valor, 10)) {
        return false;
      }

      if (this.naoComecaCom(valor, "20")) {
        return false;
      }

      var base = valor.substring(0, valor.length - 1);

      var multiplicadores = this.serie(2, 9);
      if (this.tamanhoE(valor, 10)) {
        multiplicadores.push(10);
      }

      var resto = (this.mod(base, multiplicadores) * 10) % 11;
      var digito = resto === 10 ? 0 : resto;

      return valor === base + digito;
    },

    ap: (valor): boolean => {
      if (this.tamanhoNaoE(valor)) {
        return false;
      }

      if (this.naoComecaCom(valor, "03")) {
        return false;
      }

      var base = this.primeiros(valor);

      var p, d;

      if (this.entre(base, 3000001, 3017000)) {
        p = 5;
        d = 0;
      } else if (this.entre(base, 3017001, 3019022)) {
        p = 9;
        d = 1;
      } else {
        p = 0;
        d = 0;
      }

      var resto = this.mod(p + base, [2, 3, 4, 5, 6, 7, 8, 9, 1]);

      var digito;
      if (resto === 1) {
        digito = 0;
      } else if (resto === 0) {
        digito = d;
      } else {
        digito = 11 - resto;
      }

      return valor === base + digito;
    },
    rr: (valor): boolean => {
      if (this.tamanhoNaoE(valor)) {
        return false;
      }

      if (this.naoComecaCom(valor, "24")) {
        return false;
      }

      var base = this.primeiros(valor);
      var digito = this.mod(base, [8, 7, 6, 5, 4, 3, 2, 1], 9);

      return valor === base + digito;
    },
    am: (valor): boolean => {
      if (this.tamanhoNaoE(valor)) {
        return false;
      }

      return this.calculoTrivial(valor);
    },
    ro: (valor): boolean => {
      if (!/^\d{14}$/.test(valor)) return false;

      const base = valor.substring(0, 13);
      const pesos = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

      let soma = 0;
      for (let i = 0; i < 13; i++) {
        soma += parseInt(base.charAt(i)) * pesos[i];
      }

      let resto = soma % 11;
      let digito = 11 - resto;
      if (digito === 10 || digito === 11) digito = 0;

      return valor === base + digito;
    },
    rj: (valor): boolean => {
      if (this.tamanhoNaoE(valor, 8)) {
        return false;
      }

      var base = this.primeiros(valor, 7);
      var digito = this.substracaoPor11SeMaiorQue2CasoContrario0(
        this.mod(base, this.serie(2, 7))
      );

      return valor === base + digito;
    },

    sc: (valor): boolean => {
      return this.calculoTrivial(valor);
    },

    pi: (valor): boolean => {
      return this.calculoTrivial(valor);
    },

    es: (valor): boolean => {
      return this.calculoTrivial(valor);
    },
    pr: (valor): boolean => {
      if (this.tamanhoNaoE(valor, 10)) {
        return false;
      }

      // 9092603293
      var base = this.primeiros(valor);

      var restoPrimeiro = this.mod(base, this.serie(2, 7));
      var primeiro = 11 - restoPrimeiro >= 10 ? 0 : 11 - restoPrimeiro;

      var restoSegundo = this.mod(base + primeiro, this.serie(2, 7));
      var segundo = 11 - restoSegundo >= 10 ? 0 : 11 - restoSegundo;

      return valor === base + primeiro + segundo;
    },
    pa: (valor: string): boolean => {
      // 1. Remove caracteres não numéricos
      const digits = valor.replace(/\D/g, '');

      // 2. Verifica se tem 9 dígitos
      if (digits.length !== 9) {
        return false;
      }

      // 3. Verifica se inicia com 15 ou 75 (prefixos comuns)
      if (!/^15|^75/.test(digits)) {
        return false;
      }

      // 4. Cálculo do Dígito Verificador (Módulo 11)
      const base = digits.substring(0, 8);
      const digit = parseInt(digits.substring(8, 9), 10);

      let sum = 0;
      let weight = 9;

      for (let i = 0; i < 8; i++) {
        sum += parseInt(base[i], 10) * weight;
        weight--;
      }

      const rest = sum % 11;
      const calculatedDigit = rest < 2 ? 0 : 11 - rest;

      return calculatedDigit === digit;
    },

    ce: (valor): boolean => {
      if (this.tamanhoNaoE(valor)) {
        return false;
      }

      return this.calculoTrivial(valor);
    },
    pe: (valor): boolean => {
      var base = valor.substring(0, valor.length - 2);

      var restoPrimeiro = this.mod(base);
      var primeiro = 11 - restoPrimeiro >= 10 ? 0 : 11 - restoPrimeiro;

      var restoSegundo = this.mod(base + primeiro);
      var segundo = 11 - restoSegundo >= 10 ? 0 : 11 - restoSegundo;

      return valor === base + primeiro + segundo;
    },

    ma: (valor): boolean => {
      if (this.tamanhoNaoE(valor)) {
        return false;
      }

      if (this.naoComecaCom(valor, "12")) {
        return false;
      }

      return this.calculoTrivial(valor);
    },
    ac: (valor): boolean => {
      if (this.tamanhoNaoE(valor, 13)) {
        return false;
      }

      if (this.naoComecaCom(valor, "01")) {
        return false;
      }

      var base = this.primeiros(valor, 11);

      var primeiroDigito = this.substracaoPor11SeMaiorQue2CasoContrario0(
        this.mod(base)
      );
      var segundoDigito = this.substracaoPor11SeMaiorQue2CasoContrario0(
        this.mod(base + primeiroDigito)
      );

      return valor === base + primeiroDigito + segundoDigito;
    },

    rs: (valor): boolean => {
      if (this.tamanhoNaoE(valor, 10)) {
        return false;
      }

      var base = this.primeiros(valor, 9);
      return this.calculoTrivial(valor, base, true);
    },
    mt: (valor): boolean => {
      if (this.tamanhoNaoE(valor, 11) && this.tamanhoNaoE(valor)) {
        return false;
      }

      var base = this.tamanhoE(valor, 11)
        ? valor.substring(0, 10)
        : this.primeiros(valor);
      return this.calculoTrivial(valor, base, true);
    },

    sp: (valor): boolean => {
      valor = valor.toUpperCase();

      var segundaBase;

      if (valor.substr(0, 1) === "P") {
        if (this.tamanhoNaoE(valor, 13)) {
          return false;
        }

        var base = valor.substring(1, 9);
        segundaBase = valor.substring(10, 13);
        var resto = this.mod(base, [10, 8, 7, 6, 5, 4, 3, 1]).toString();
        var digito = resto.length > 1 ? resto[1] : resto[0];

        return valor === "P" + base + digito + segundaBase;
      } else {
        if (this.tamanhoNaoE(valor, 12)) {
          return false;
        }

        var primeiraBase = this.primeiros(valor);
        segundaBase = valor.substring(9, 11);

        var primeiroResto = this.mod(
          primeiraBase,
          [10, 8, 7, 6, 5, 4, 3, 1]
        ).toString();
        var primeiro =
          primeiroResto.length > 1 ? primeiroResto[1] : primeiroResto[0];

        var segundoResto = this.mod(
          primeiraBase + primeiro + segundaBase,
          this.serie(2, 10)
        ).toString();
        var segundo =
          segundoResto.length > 1 ? segundoResto[1] : segundoResto[0];

        return valor === primeiraBase + primeiro + segundaBase + segundo;
      }
    },
    mg: (valor): boolean => {
      if (this.tamanhoNaoE(valor, 13)) {
        return false;
      }

      var base = this.primeiros(valor, 11);

      var baseComZero = valor.substring(0, 3) + "0" + valor.substring(3, 11);

      var i = 0;
      var produtorioLiteral = baseComZero
        .split("")
        .reduceRight(function (anterior, atual) {
          if (i > [2, 1].length - 1) {
            i = 0;
          }

          return (
            ([2, 1][i++] * parseInt(atual, 10)).toString() + anterior.toString()
          );
        }, "")
        .split("")
        .reduce(function (anterior, atual) {
          return anterior + parseInt(atual);
        }, 0);

      // var primeiro = (produtorioLiteral / 10 + 1) * 10 - produtorioLiteral;
      let primeiro = Math.ceil(produtorioLiteral / 10) * 10 - produtorioLiteral;
      if (primeiro === 10) {
        primeiro = 0;
      }

      var segundo = this.substracaoPor11SeMaiorQue2CasoContrario0(
        this.mod(base + primeiro, this.serie(2, 11))
      );
      return valor === base + primeiro + segundo;
    },

    to: (valor): boolean => {
      if (this.tamanhoNaoE(valor) && this.tamanhoNaoE(valor, 11)) {
        return false;
      }

      var base;

      if (this.tamanhoE(valor, 11)) {
        if (["01", "02", "03", "99"].indexOf(valor.substring(2, 4)) === -1) {
          return false;
        }

        base = valor.substring(0, 2) + valor.substring(4, 10);
      } else {
        base = this.primeiros(valor);
      }

      var digito = this.substracaoPor11SeMaiorQue2CasoContrario0(
        this.mod(base)
      );

      return valor === valor.substring(0, valor.length - 1) + digito;
    },
    go: (valor): boolean => {
      const ie = valor.replace(/\D/g, "");

      // Verifica se o tamanho é 9
      if (ie.length !== 9) {
        return false;
      }

      // Verifica se os dois primeiros dígitos são válidos (10 ou 11 ou entre 20 e 29)
      const primeiraParte = parseInt(ie.substring(0, 2), 10);
      if (
        !(
          primeiraParte === 10 ||
          primeiraParte === 11 ||
          (primeiraParte >= 20 && primeiraParte <= 29)
        )
      ) {
        return false;
      }

      // Cálculo do dígito verificador
      let soma = 0;
      const peso = [9, 8, 7, 6, 5, 4, 3, 2];

      for (let i = 0; i < 8; i++) {
        soma += parseInt(ie.charAt(i), 10) * peso[i];
      }

      const resto = soma % 11;
      let digitoVerificador;

      if (resto === 0 || resto === 1) {
        digitoVerificador = 0;
      } else {
        digitoVerificador = 11 - resto;
      }

      // Verifica se o dígito verificador é igual ao último dígito da inscrição estadual
      return digitoVerificador === parseInt(ie.charAt(8), 10);
    },

    ms: (valor): boolean => {
      if (this.naoComecaCom(valor, "28")) {
        return false;
      }

      return this.calculoTrivial(valor);
    },

    df: (valor): boolean => {
      if (this.tamanhoNaoE(valor, 13)) {
        return false;
      }

      var base = this.primeiros(valor, 11);

      var primeiro = this.substracaoPor11SeMaiorQue2CasoContrario0(
        this.mod(base)
      );
      var segundo = this.substracaoPor11SeMaiorQue2CasoContrario0(
        this.mod(base + primeiro)
      );

      return valor === base + primeiro + segundo;
    },
  };
  private lookup(ie: string): string[] | false {
    const resultado = [];
    for (const estado in this.funcoes) {
      if (this.funcoes[estado](ie)) {
        resultado.push(estado);
      }
    }
    return this.tamanhoE(resultado, 0) ? false : resultado;
  }

  validar(
    ie: string | string[],
    estado?: string
  ): boolean | string[] | boolean[] {
    if (this.eIndefinido(estado) || estado === null) {
      estado = "";
    }

    estado = estado.toLowerCase();

    if (estado !== "" && !(estado in this.funcoes)) {
      return false;
    }

    if (this.eIndefinido(ie)) {
      return false;
    }

    // if (Array.isArray(ie)) {
    //   return ie.map(val => this.validar(val, estado));
    // }

    if (typeof ie !== "string") {
      return false;
    }

    if (ie.match(/^ISENTO$/i)) {
      return true;
    }

    ie = ie.replace(/[\.|\-|\/|\s]/g, "");

    if (estado === "") {
      return this.lookup(ie);
    }

    if (/^\d+$/.test(ie) || estado === "sp") {
      return this.funcoes[estado](ie);
    }

    return false;
  }
}
